import { useState, useMemo, useCallback } from 'react';
import { diffLines, type Change } from 'diff';
import { useTokenizedLines, type TokenLine } from '../hooks/use-shiki.js';

interface DiffViewerProps {
  filePath: string;
  oldContent: string;
  newContent: string;
}

const CONTEXT_LINES = 3;

interface DiffSection {
  type: 'context' | 'change' | 'collapsed';
  lines: { type: 'added' | 'removed' | 'unchanged'; content: string; oldLineNo?: number; newLineNo?: number }[];
  collapsedCount?: number;
}

function computeDiffSections(changes: Change[]): DiffSection[] {
  const allLines: DiffSection['lines'][number][] = [];
  let oldLine = 1;
  let newLine = 1;

  for (const change of changes) {
    const lines = change.value.replace(/\n$/, '').split('\n');
    for (const line of lines) {
      if (change.added) {
        allLines.push({ type: 'added', content: line, newLineNo: newLine++ });
      } else if (change.removed) {
        allLines.push({ type: 'removed', content: line, oldLineNo: oldLine++ });
      } else {
        allLines.push({ type: 'unchanged', content: line, oldLineNo: oldLine++, newLineNo: newLine++ });
      }
    }
  }

  // Group into sections with context collapsing
  const sections: DiffSection[] = [];
  let i = 0;

  while (i < allLines.length) {
    const line = allLines[i];

    if (line.type !== 'unchanged') {
      // Collect the change block with context
      const changeLines: DiffSection['lines'] = [];

      // Add leading context
      const contextStart = Math.max(0, i - CONTEXT_LINES);
      if (sections.length > 0 && contextStart < i) {
        // Pull back context lines from previous collapsed section
        const prev = sections[sections.length - 1];
        if (prev.type === 'collapsed' && prev.lines.length > 0) {
          const pullCount = Math.min(CONTEXT_LINES, prev.lines.length);
          const pulled = prev.lines.splice(prev.lines.length - pullCount, pullCount);
          prev.collapsedCount = prev.lines.length;
          if (prev.lines.length === 0) sections.pop();
          changeLines.push(...pulled);
        }
      }

      // Add changed lines
      while (i < allLines.length && allLines[i].type !== 'unchanged') {
        changeLines.push(allLines[i]);
        i++;
      }

      // Add trailing context
      let trailingCount = 0;
      while (i < allLines.length && allLines[i].type === 'unchanged' && trailingCount < CONTEXT_LINES) {
        changeLines.push(allLines[i]);
        i++;
        trailingCount++;
      }

      sections.push({ type: 'change', lines: changeLines });
    } else {
      // Collect unchanged lines for collapsing
      const unchangedLines: DiffSection['lines'] = [];
      while (i < allLines.length && allLines[i].type === 'unchanged') {
        unchangedLines.push(allLines[i]);
        i++;
      }
      sections.push({ type: 'collapsed', lines: unchangedLines, collapsedCount: unchangedLines.length });
    }
  }

  return sections;
}

function getLanguageFromPath(filePath: string): string | undefined {
  const ext = filePath.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript',
    py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
    hcl: 'hcl', tf: 'hcl', yml: 'yaml', yaml: 'yaml',
    json: 'json', css: 'css', html: 'html', md: 'markdown',
    sh: 'bash', bash: 'bash', zsh: 'bash',
  };
  return ext ? map[ext] : undefined;
}

function UnifiedDiff({ changes, filePath, oldTokens, newTokens }: { changes: Change[]; filePath: string; oldTokens: TokenLine[] | null; newTokens: TokenLine[] | null }) {
  const sections = useMemo(() => computeDiffSections(changes), [changes]);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const lang = getLanguageFromPath(filePath);

  function toggleSection(idx: number) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  return (
    <div className={`overflow-x-auto text-xs font-mono ${lang ? `language-${lang}` : ''}`}>
      <table className="w-full border-collapse">
        <tbody>
          {sections.map((section, sIdx) => {
            if (section.type === 'collapsed' && !expandedSections.has(sIdx)) {
              return (
                <tr key={sIdx}>
                  <td colSpan={3} className="px-2 py-1 text-center" style={{ backgroundColor: 'var(--color-surface-2)' }}>
                    <button
                      type="button"
                      onClick={() => toggleSection(sIdx)}
                      className="text-xs" style={{ color: 'var(--color-brand)' }}
                    >
                      Show {section.collapsedCount} unchanged lines
                    </button>
                  </td>
                </tr>
              );
            }

            const lines = section.type === 'collapsed' ? section.lines : section.lines;
            return lines.map((line, lIdx) => {
              const bgColor = line.type === 'added'
                ? 'var(--color-success-dim)'
                : line.type === 'removed'
                  ? 'var(--color-error-dim)'
                  : undefined;
              const textColor = line.type === 'added'
                ? 'var(--color-success)'
                : line.type === 'removed'
                  ? 'var(--color-error)'
                  : 'var(--color-text-2)';
              const prefix = line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ';
              const prefixColor = line.type === 'added'
                ? 'var(--color-success)'
                : line.type === 'removed'
                  ? 'var(--color-error)'
                  : 'var(--color-text-3)';

              // Get tokens for this line: removed lines from old, added/unchanged from new
              const lineTokens = line.type === 'removed'
                ? (line.oldLineNo != null ? oldTokens?.[line.oldLineNo - 1] : null)
                : (line.newLineNo != null ? newTokens?.[line.newLineNo - 1] : null);

              return (
                <tr key={`${sIdx}-${lIdx}`} style={bgColor ? { backgroundColor: bgColor } : undefined}>
                  <td className="w-10 select-none border-r px-2 text-right" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-3)' }}>
                    {line.oldLineNo ?? ''}
                  </td>
                  <td className="w-10 select-none border-r px-2 text-right" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-3)' }}>
                    {line.newLineNo ?? ''}
                  </td>
                  <td className="whitespace-pre px-2 py-0.5" style={{ color: textColor }}>
                    <span className="select-none" style={{ color: prefixColor }}>
                      {prefix}
                    </span>
                    {' '}
                    {lineTokens
                      ? lineTokens.map((t, i) => <span key={i} style={t.color ? { color: t.color } : undefined}>{t.content}</span>)
                      : line.content}
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
}

function DiffHeader({ filePath, stats, newContent }: { filePath: string; stats: { added: number; removed: number }; newContent: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(newContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [newContent]);

  return (
    <div className="flex items-center justify-between border-b px-3 py-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-2)' }}>
      <span className="truncate font-mono text-xs" style={{ color: 'var(--color-text-2)' }}>{filePath}</span>
      <div className="flex items-center gap-2 text-xs">
        {stats.added > 0 && <span className="font-medium" style={{ color: 'var(--color-success)' }}>+{stats.added}</span>}
        {stats.removed > 0 && <span className="font-medium" style={{ color: 'var(--color-error)' }}>-{stats.removed}</span>}
        <button
          type="button"
          onClick={copy}
          className="rounded p-0.5"
          style={{ color: copied ? 'var(--color-success)' : 'var(--color-text-3)' }}
          title="Copy new content"
          aria-label="Copy new content"
        >
          {copied ? (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

export function DiffViewer({ filePath, oldContent, newContent }: DiffViewerProps) {
  const lang = getLanguageFromPath(filePath);
  const oldTokens = useTokenizedLines(oldContent, lang);
  const newTokens = useTokenizedLines(newContent, lang);
  const changes = useMemo(() => diffLines(oldContent, newContent), [oldContent, newContent]);

  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const change of changes) {
      const lineCount = change.value.replace(/\n$/, '').split('\n').length;
      if (change.added) added += lineCount;
      if (change.removed) removed += lineCount;
    }
    return { added, removed };
  }, [changes]);

  if (stats.added === 0 && stats.removed === 0) {
    return (
      <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text-3)' }}>
        No changes in {filePath}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--color-border)' }} data-testid="diff-viewer">
      {/* Header */}
      <DiffHeader filePath={filePath} stats={stats} newContent={newContent} />

      {/* Diff body */}
      <UnifiedDiff changes={changes} filePath={filePath} oldTokens={oldTokens} newTokens={newTokens} />
    </div>
  );
}
